import { Wallet, Target, TrendingUp, AlertCircle, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { useFetch } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const eur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", useGrouping: "always" }).format(v || 0);
const monthLabel = (d) => new Date(d).toLocaleDateString("it-IT", { month: "short", year: "2-digit" });

const Stat = ({ label, value, testid, accent }) => (
  <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
    <p className="text-[10px] uppercase tracking-[0.18em] text-warm-text">{label}</p>
    <p data-testid={testid} className={`font-serif text-2xl sm:text-3xl mt-1 ${accent || ""}`}>{value}</p>
  </div>
);

const Bar = ({ value }) => (
  <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
    <div className="h-full rounded-full bg-gradient-to-r from-gold-dark to-gold-light transition-[width] duration-1000" style={{ width: `${Math.round(value * 100)}%` }} />
  </div>
);

function Agevolazione({ k }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
      <Stat label="Obiettivo annuale" value={eur(k.annual_target)} />
      <Stat label="Speso nel contratto" value={eur(k.spent)} testid="spending-contract-spent" />
      <Stat label="Ti rimane da spendere" value={eur(k.remaining)} testid="spending-remaining" accent="text-gold-gradient" />
    </div>
  );
}

function Partner({ k }) {
  const dl = new Date(k.deadline).toLocaleDateString("it-IT", { day: "numeric", month: "long" });
  return (
    <>
      {!k.not_started && !k.ended && (
        <p data-testid="spending-deadline" className={`mt-5 inline-flex flex-wrap items-center gap-2 rounded-full border px-4 py-2 text-xs ${(k.overdue || k.days_left <= 3) && k.to_spend_this_month > 0 ? "border-[#F06A55]/60 bg-[#F06A55]/10 text-[#F6A496]" : "border-gold/40 bg-gold/10 text-gold-light"}`}>
          <CalendarClock className="w-3.5 h-3.5" /> {k.overdue ? `Scadenza del ${dl} superata · ordina e paga subito` : k.to_spend_this_month === 0 ? `Mese coperto · scadenza ${dl}` : `Ordina e paga entro il ${dl} · ${k.days_left === 0 ? "scade oggi" : `mancano ${k.days_left} ${k.days_left === 1 ? "giorno" : "giorni"}`}`}
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <Stat label="Quota mensile" value={eur(k.monthly_quota)} />
        <Stat label={k.delay > 0 ? "Ritardo accumulato" : "In anticipo di"} value={eur(k.delay > 0 ? k.delay : k.advance)} testid="spending-delay" accent={k.delay > 0 ? "text-[#F06A55]" : "text-[#5DCB8A]"} />
        <Stat label="Pagato questo mese" value={eur(k.current_month.spent)} />
        <Stat label="Da ordinare e pagare" value={k.not_started ? "Dal " + fmtDate(k.start) : eur(k.to_spend_this_month)} testid="spending-to-spend-month" accent="text-gold-gradient" />
      </div>
      <div className="mt-5 grid grid-cols-6 sm:grid-cols-12 gap-1.5" data-testid="spending-months">
        {k.months.map((m, i) => {
          const ok = m.filled >= m.quota;
          const pct = Math.min(100, (m.filled / (m.quota || 1)) * 100);
          return (
            <div key={m.deadline} data-testid={`spending-month-${i}`} title={`${monthLabel(m.deadline)}: coperto ${eur(m.filled)} su ${eur(m.quota)} · ordini pagati nel mese ${eur(m.spent)}`} className="text-center">
              <div className={`h-14 rounded-md flex items-end overflow-hidden ${m.status === "current" ? "ring-1 ring-gold/60" : ""} bg-white/[0.05]`}>
                <div className={`w-full transition-[height] duration-700 ${ok ? "bg-gold" : "bg-gold/40"}`} style={{ height: `${pct}%` }} />
              </div>
              <p className={`text-[9px] mt-1 uppercase ${m.status === "current" ? "text-gold" : "text-warm-text"}`}>{monthLabel(m.deadline)}</p>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-warm-text">Come una clessidra: il totale pagato riempie i mesi uno dopo l'altro, una quota alla volta.</p>
    </>
  );
}

export function SpendingHome() {
  const { data } = useFetch("/shop/spending");
  if (!data?.available || !data.linked) return null;
  const k = data.contract;
  return (
    <Link to="/ordini" data-testid="home-spending" className="relative mt-8 inline-flex flex-wrap items-end gap-x-8 gap-y-3 lux-card border-gold/30 px-6 py-5 hover:border-gold/60 transition-colors">
      <div>
        <p className="eyebrow flex items-center gap-2"><Wallet className="w-4 h-4 text-gold" strokeWidth={1.4} /> Hai speso con Skin Lovers</p>
        <p data-testid="home-spending-amount" className="font-serif text-4xl sm:text-5xl mt-2">{eur(data.last_12_months)}</p>
        <p className="text-xs text-warm-text mt-1">negli ultimi 12 mesi · senza IVA e spedizione</p>
      </div>
      {k?.type === "official_partner" && !k.ended && !k.not_started && (
        <div data-testid="home-spending-partner">
          <p className="text-[10px] uppercase tracking-[0.18em] text-warm-text">{k.to_spend_this_month === 0 ? "Questo mese sei già coperto" : k.overdue ? `Scaduto il ${fmtDate(k.deadline)} · da pagare subito` : `Da ordinare e pagare entro il ${fmtDate(k.deadline)}`}</p>
          <p className="font-serif text-2xl text-gold mt-1">{k.to_spend_this_month === 0 ? `In anticipo di ${eur(k.advance)}` : eur(k.to_spend_this_month)}</p>
        </div>
      )}
      {k?.type === "agevolazione" && k.active && (
        <div data-testid="home-spending-agevolazione">
          <p className="text-[10px] uppercase tracking-[0.18em] text-warm-text">Ti mancano per l'obiettivo</p>
          <p className="font-serif text-2xl text-gold mt-1">{eur(k.remaining)}</p>
        </div>
      )}
    </Link>
  );
}


export function SpendingCard({ endpoint, admin }) {
  const { data } = useFetch(endpoint);
  if (!data?.available) return null;
  const k = data.contract;
  return (
    <section data-testid="spending-card" className="lux-card p-6 sm:p-8 animate-fade-up">
      <div className="flex items-center gap-3"><Wallet className="w-5 h-5 text-gold" strokeWidth={1.4} /><p className="eyebrow">{admin ? "Spesa del cliente" : "La tua spesa con Skin Lovers"}</p></div>
      {!data.linked ? (
        <p className="mt-4 text-sm text-warm-text">{admin ? "Nessun cliente Shopify collegato: inserisci lo Shopify Customer ID nella scheda." : "Il tuo account del negozio non è ancora collegato. Ci pensiamo noi."}</p>
      ) : (
        <>
          <p data-testid="spending-last-12" className="font-serif text-4xl sm:text-5xl mt-4">{eur(data.last_12_months)}</p>
          <p className="text-xs text-warm-text mt-1">negli ultimi 12 mesi · senza IVA e spedizione</p>
          {admin && !data.full_history && (
            <p data-testid="spending-history-warning" className="mt-3 inline-flex items-center gap-2 text-xs text-gold-light"><AlertCircle className="w-3.5 h-3.5" /> Shopify mostra solo gli ultimi 60 giorni finché non attivi lo storico completo (read_all_orders).</p>
          )}
          {k && (
            <div className="hairline mt-6 pt-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="inline-flex items-center gap-2 text-sm font-semibold">
                  {k.type === "official_partner" ? <TrendingUp className="w-4 h-4 text-gold" /> : <Target className="w-4 h-4 text-gold" />}
                  {k.type === "official_partner" ? "Contratto Centro Official Partner" : "Contratto di agevolazione"}
                </p>
                <p className="text-xs text-warm-text">dal {fmtDate(k.start)} al {fmtDate(k.end)}{k.count_from && k.count_from !== k.start ? ` · ordini contati dal ${fmtDate(k.count_from)}` : ""}</p>
              </div>
              <div className="mt-4"><Bar value={k.progress} /></div>
              <p className="text-xs text-warm-text mt-2">{eur(k.spent)} su {eur(k.annual_target)} · {Math.round(k.progress * 100)}%</p>
              {k.type === "official_partner" && <p className="text-[11px] text-warm-text mt-1">Per gli Official Partner contano gli ordini pagati del mese (dal 1 all'ultimo giorno): l'ordine va fatto e pagato entro il 7.</p>}
              {k.type === "official_partner" ? <Partner k={k} /> : <Agevolazione k={k} />}
            </div>
          )}
        </>
      )}
    </section>
  );
}
